// ═══════════════════════════════════════════════════════════════
// GeoOps — Zone Overview: all zones on one map with data layers
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Paper, Text, Group, Badge, Button, Switch, Stack,
  Center, Loader, ThemeIcon, Title, Divider, ActionIcon, Tooltip,
} from '../../components/gab/GabComponents';
import {
  IconArrowLeft, IconAlertTriangle, IconBuilding, IconPhone, IconMail,
  IconMapPin, IconLayersLinked, IconCamera, IconRoadSign, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { fetchCamerasNearby, type TrafficCamera } from '../../utils/perimeterCameras';
import { scanIntersections, scanPOIs } from '../../utils/perimeterOverpass';
import type { PerimeterClosure, PerimeterPOI } from '../../utils/perimeterGeo';
import { POI_CATEGORIES } from '../../utils/perimeterConstants';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, LayersControl, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuthStore } from '../../store/authStore';
import { useGeoOpsStore, ZONE_TYPE_META } from '../../store/geoopsStore';
import type { GeoOpsZone } from '../../store/geoopsStore';
import { useGeoOpsDataStore } from '../../store/geoopsDataStore';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import IncidentLocationMarker from '../../components/map/IncidentLocationMarker';
import AutoRefreshImage from '../../components/map/AutoRefreshImage';
import { zoneBounds, isInsideZone } from '../../lib/geoopsGeo';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useShelterStore } from '../../store/shelterStore';
import { useImpactStore } from '../../store/impactStore';
import { SECTOR_COLORS } from '../mainstreet/constants';
import { SHELTER_STATUS_COLORS } from '../../types/shelter';
import { formatPhone } from '../../lib/phoneFormat';

function parseCoords(s: string): { lat: number; lng: number } | null {
  if (!s) return null;
  const p = s.split(',').map(v => parseFloat(v.trim()));
  return p.length >= 2 && !isNaN(p[0]) && !isNaN(p[1]) ? { lat: p[0], lng: p[1] } : null;
}

function FitAllZones({ zones, fallbackCenter }: { zones: GeoOpsZone[]; fallbackCenter?: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    const allBounds: L.LatLngBounds[] = [];
    zones.forEach(z => {
      if (z.geometry) {
        const b = zoneBounds(z.geometry);
        if (b) allBounds.push(b);
      }
    });
    if (allBounds.length > 0) {
      let combined = allBounds[0];
      allBounds.slice(1).forEach(b => { combined = combined.extend(b); });
      map.fitBounds(combined, { padding: [40, 40] });
    } else if (fallbackCenter) {
      // No zones drawn yet — center on the incident location
      map.setView(fallbackCenter, 13, { animate: true });
    }
  }, [zones, map, fallbackCenter]);
  return null;
}

const SEVERITY_COLORS: Record<string, string> = {
  affected: '#16a34a', minor: '#d97706', major: '#ea580c', destroyed: '#B12525',
};

export default function ZoneOverview() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { zones, isLoading, loadZones } = useGeoOpsStore();
  const { cameras: cameraSources, loadCameras } = useGeoOpsDataStore();

  const [showLayerPanel, setShowLayerPanel] = useState(true);
  const [showBusinesses, setShowBusinesses] = useState(true);
  const [showShelters, setShowShelters] = useState(false);
  const [showAssessments, setShowAssessments] = useState(false);
  const [showPois, setShowPois] = useState(false);
  const [showCameras, setShowCameras] = useState(false);
  const [showClosures, setShowClosures] = useState(false);

  // Cameras & road closures (each scan-all populates these)
  const [cameras, setCameras] = useState<TrafficCamera[]>([]);
  const [closures, setClosures] = useState<PerimeterClosure[]>([]);
  const [extraLoading, setExtraLoading] = useState(false);

  // POI state — uses PerimeterPOI from scanPOIs which already runs through
  // our priority-order matcher with title/keyword filtering and the noise
  // vetoes (drops gyms, vape shops, etc.). Same engine as ZoneDetail uses.
  type POI = PerimeterPOI;
  const [pois, setPois] = useState<POI[]>([]);
  const [poiLoading, setPoiLoading] = useState(false);

  const scanAllZones = useCallback(async () => {
    const incidentIdStr = String(activeIncident?.id || '');
    const allZones = useGeoOpsStore.getState().zones.filter(z => String(z.incidentId) === incidentIdStr);
    if (allZones.length === 0) {
      notifications.show({ message: 'No zones to scan in this incident', color: '#C68700' });
      return;
    }
    setShowPois(true);
    setShowCameras(true);
    setShowClosures(true);
    setPoiLoading(true);
    setExtraLoading(true);

    const mergedPois = new Map<string, POI>();
    const mergedCams = new Map<number, TrafficCamera>();
    const mergedClosures = new Map<string, PerimeterClosure>();
    let scanned = 0;
    let failed = 0;

    try {
      for (const zone of allZones) {
        if (!zone.geometry) continue;
        const bounds = zoneBounds(zone.geometry);
        if (!bounds) continue;
        const center = bounds.getCenter();
        const radiusM = Math.min(
          Math.max(
            center.distanceTo(bounds.getNorthEast()),
            center.distanceTo(bounds.getSouthWest()),
          ),
          20000,
        );
        const radiusMi = radiusM / 1609.34;

        // POIs — use scanPOIs which does per-category fetching + smart bucketing
        try {
          const items = await scanPOIs({ lat: center.lat, lng: center.lng }, radiusMi);
          for (const p of items) {
            if (!mergedPois.has(p.id)) mergedPois.set(p.id, p);
          }
        } catch { /* fall through */ }

        // Cameras (511) — runs in parallel with closures
        // Closures (Overpass)
        const [camResult, closureResult] = await Promise.all([
          fetchCamerasNearby({ lat: center.lat, lng: center.lng }, radiusMi, cameraSources).catch(() => []),
          scanIntersections({ lat: center.lat, lng: center.lng }, radiusMi).catch(() => []),
        ]);
        for (const c of camResult) {
          if (!mergedCams.has(c.id)) mergedCams.set(c.id, c);
        }
        for (const c of closureResult) {
          if (!mergedClosures.has(c.id)) mergedClosures.set(c.id, c);
        }

        if (camResult.length === 0 && closureResult.length === 0) {
          // not necessarily a failure — just nothing in that zone
        }
        scanned++;
      }
      setPois(Array.from(mergedPois.values()));
      setCameras(Array.from(mergedCams.values()));
      setClosures(Array.from(mergedClosures.values()));
      notifications.show({
        message: `Scanned ${scanned} zone${scanned !== 1 ? 's' : ''}: ${mergedPois.size} POIs, ${mergedCams.size} cameras, ${mergedClosures.size} road intersections${failed > 0 ? ` (${failed} failed)` : ''}`,
        color: 'var(--color-success)', autoClose: 6000,
      });
    } finally {
      setPoiLoading(false);
      setExtraLoading(false);
    }
  }, [activeIncident]);

  const { organizations, loadOrganizations } = useMainStreetStore();
  const { shelters, loadShelters } = useShelterStore();
  const { assessments, loadAssessments } = useImpactStore();

  useEffect(() => {
    if (activeIncident) loadZones(String(activeIncident.id));
    loadCameras();
  }, [activeIncident]);
  useEffect(() => { if (showBusinesses) loadOrganizations(); }, [showBusinesses]);
  useEffect(() => { if (showShelters) loadShelters(); }, [showShelters]);
  useEffect(() => { if (showAssessments) loadAssessments(); }, [showAssessments]);

  const incidentZones = useMemo(
    () => zones.filter(z => String(z.incidentId) === String(activeIncident?.id)),
    [zones, activeIncident]
  );
  const activeZones = useMemo(() => incidentZones.filter(z => z.status === 'active'), [incidentZones]);

  const orgMarkers = useMemo(() => {
    if (!showBusinesses) return [];
    return organizations.map(o => {
      const c = parseCoords(o.coordinates);
      return c ? { org: o, ...c } : null;
    }).filter(Boolean) as { org: typeof organizations[0]; lat: number; lng: number }[];
  }, [organizations, showBusinesses]);

  const orgsInZone = useMemo(() => {
    if (activeZones.length === 0) return new Set<number>();
    const s = new Set<number>();
    orgMarkers.forEach(m => {
      if (activeZones.some(z => z.geometry && isInsideZone(m.lat, m.lng, z.geometry))) s.add(m.org.id);
    });
    return s;
  }, [orgMarkers, activeZones]);

  // Initial map center — incident lat/lng with Atlanta fallback
  const incidentCenter = useMemo<[number, number]>(() => {
    const lat = parseFloat(activeIncident?.latitude || '');
    const lng = parseFloat(activeIncident?.longitude || '');
    if (isFinite(lat) && isFinite(lng) && !(lat === 0 && lng === 0)) return [lat, lng];
    return [33.749, -84.388];
  }, [activeIncident]);

  // POIs caught inside ANY incident zone (with zone-name annotation)
  const poisInZone = useMemo(() => {
    if (incidentZones.length === 0 || pois.length === 0) return [] as Array<POI & { zoneNames: string[] }>;
    const out: Array<POI & { zoneNames: string[] }> = [];
    for (const p of pois) {
      const matched: string[] = [];
      for (const z of incidentZones) {
        if (z.geometry && isInsideZone(p.lat, p.lng, z.geometry)) {
          matched.push(z.name || `Zone ${z.id}`);
        }
      }
      if (matched.length > 0) out.push({ ...p, zoneNames: matched });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois, incidentZones]);

  // Group captured POIs by category, ordered by POI_CATEGORIES priority.
  // Used by the categorized panel so users see "Hospitals (3), Gas Stations (5), ..."
  // instead of a flat list dominated by whichever category has the most hits.
  const poisInZoneByCategory = useMemo(() => {
    const byCat = new Map<string, { label: string; icon: string; items: typeof poisInZone }>();
    for (const p of poisInZone) {
      const key = p.category || 'other';
      if (!byCat.has(key)) {
        byCat.set(key, { label: p.categoryLabel || 'Other', icon: p.categoryIcon || '📍', items: [] });
      }
      byCat.get(key)!.items.push(p);
    }
    // Sort each bucket's items by name and order the buckets by POI_CATEGORIES priority
    const orderIndex = new Map<string, number>();
    POI_CATEGORIES.forEach((c, i) => orderIndex.set(c.key, i));
    const groups = Array.from(byCat.entries()).map(([key, val]) => ({
      key, ...val, items: [...val.items].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    }));
    groups.sort((a, b) => (orderIndex.get(a.key) ?? 999) - (orderIndex.get(b.key) ?? 999));
    return groups;
  }, [poisInZone]);

  // Cameras inside any zone, with zone-name annotation
  const camerasInZone = useMemo(() => {
    if (incidentZones.length === 0 || cameras.length === 0) return [] as Array<TrafficCamera & { zoneNames: string[] }>;
    const out: Array<TrafficCamera & { zoneNames: string[] }> = [];
    for (const c of cameras) {
      const matched: string[] = [];
      for (const z of incidentZones) {
        if (z.geometry && isInsideZone(c.lat, c.lng, z.geometry)) {
          matched.push(z.name || `Zone ${z.id}`);
        }
      }
      if (matched.length > 0) out.push({ ...c, zoneNames: matched });
    }
    return out;
  }, [cameras, incidentZones]);

  // Road intersections inside any zone, with zone-name annotation
  const closuresInZone = useMemo(() => {
    if (incidentZones.length === 0 || closures.length === 0) return [] as Array<PerimeterClosure & { zoneNames: string[] }>;
    const out: Array<PerimeterClosure & { zoneNames: string[] }> = [];
    for (const c of closures) {
      const matched: string[] = [];
      for (const z of incidentZones) {
        if (z.geometry && isInsideZone(c.lat, c.lng, z.geometry)) {
          matched.push(z.name || `Zone ${z.id}`);
        }
      }
      if (matched.length > 0) out.push({ ...c, zoneNames: matched });
    }
    return out;
  }, [closures, incidentZones]);

  const shelterMarkers = useMemo(() => {
    if (!showShelters) return [];
    return shelters.filter(s => s.latitude && s.longitude).map(s => ({
      shelter: s, lat: parseFloat(s.latitude), lng: parseFloat(s.longitude),
    })).filter(m => !isNaN(m.lat) && !isNaN(m.lng));
  }, [shelters, showShelters]);

  const assessmentMarkers = useMemo(() => {
    if (!showAssessments) return [];
    return assessments.filter(a => a.latitude && a.longitude);
  }, [assessments, showAssessments]);

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598"><IconAlertTriangle size={30} /></ThemeIcon>
          <Title order={3} c="black">No Incident Selected</Title>
        </Stack>
      </Center>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper p="xs" style={{ borderBottom: '1px solid #e5e7eb', zIndex: 150, flexShrink: 0 }}>
        <Group justify="space-between">
          <Group gap="xs">
            <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/geoops')}>Back</Button>
            <Text size="sm" fw={600}>Zone Overview</Text>
            <Badge size="sm" variant="light">{activeIncident.name}</Badge>
            <Badge size="sm" variant="light" color="var(--color-primary-700)">{incidentZones.length} zones</Badge>
            {orgsInZone.size > 0 && (
              <Badge size="sm" variant="light" color="var(--color-error)">{orgsInZone.size} in zone</Badge>
            )}
            {poisInZone.length > 0 && (
              <Badge size="sm" color="var(--data-viz-650)" variant="filled" leftSection={<IconMapPin size={10} />}>
                {poisInZone.length} captured
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Tooltip label="Data Layers" position="bottom">
              <ActionIcon size="md" variant={showLayerPanel ? 'filled' : 'light'}
                color={showLayerPanel ? 'var(--color-primary-700)' : '#939598'}
                onClick={() => setShowLayerPanel(!showLayerPanel)}>
                <IconLayersLinked size={16} />
              </ActionIcon>
            </Tooltip>
            <Divider orientation="vertical" />
            <Button
              size="xs" variant="filled" color="var(--data-viz-650)"
              leftSection={<IconLayersLinked size={14} />}
              loading={poiLoading || extraLoading}
              disabled={incidentZones.length === 0}
              onClick={scanAllZones}
            >
              Scan All Zones
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        {isLoading ? (
          <Center h="100%"><Loader size="md" /></Center>
        ) : (
          <MapContainer center={incidentCenter} zoom={incidentCenter[0] !== 33.749 ? 13 : 11}
            style={{ height: '100%', width: '100%' }}>
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Street">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite">
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution='&copy; Esri' />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Dark">
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; CARTO' />
              </LayersControl.BaseLayer>
            </LayersControl>

            <ZoneMapOverlay zones={incidentZones} interactive
              onZoneClick={z => navigate(`/geoops/zone/${z.id}`)} />
            <FitAllZones zones={incidentZones} fallbackCenter={incidentCenter} />

            {/* Active incident location pin */}
            <IncidentLocationMarker incident={activeIncident} />

            {/* Business markers */}
            {orgMarkers.map(({ org, lat, lng }) => {
              const inZone = orgsInZone.has(org.id);
              const color = SECTOR_COLORS[org.sectorType] || '#0891b2';
              return (
                <CircleMarker key={`biz-${org.id}`} center={[lat, lng]} radius={7}
                  pathOptions={{
                    fillColor: color, fillOpacity: 0.9, weight: inZone ? 3 : 1.5,
                    color: inZone ? '#B12525' : '#fff',
                  }}>
                  <Popup minWidth={220}>
                    <div style={{ fontSize: 14 }}>
                      <Text size="sm" fw={700} mb={2}>{org.name}</Text>
                      {org.sectorType && <Badge size="xs" variant="light" color="#03AC9E" mb={4}>{org.sectorType}</Badge>}
                      {inZone && <Badge size="xs" color="var(--color-error)" variant="light" ml={4} mb={4}>In Zone</Badge>}
                      {org.primaryContact && (
                        <Group gap={4} mb={2}><IconBuilding size={12} color="#888" /><Text size="xs">{org.primaryContact}</Text></Group>
                      )}
                      {org.phone && (
                        <Group gap={4} mb={2}><IconPhone size={12} color="#888" /><Text size="xs" component="a" href={`tel:${org.phone}`} c="var(--color-primary-700)">{formatPhone(org.phone)}</Text></Group>
                      )}
                      {org.email && (
                        <Group gap={4} mb={2}><IconMail size={12} color="#888" /><Text size="xs" component="a" href={`mailto:${org.email}`} c="var(--color-primary-700)">{org.email}</Text></Group>
                      )}
                      {org.address && <Text size="xs" c="dimmed">{org.address}{org.city ? `, ${org.city}` : ''}</Text>}
                      <Badge size="xs" variant="light" mt={4}
                        color={org.opStatus === 'operational' ? 'var(--color-success)' : org.opStatus === 'limited' ? '#C68700' : 'var(--color-error)'}>
                        {org.opStatus || 'unknown'}
                      </Badge>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

            {/* Shelter markers */}
            {shelterMarkers.map(({ shelter, lat, lng }) => (
              <CircleMarker key={`sh-${shelter.Rid}`} center={[lat, lng]} radius={9}
                pathOptions={{
                  fillColor: (SHELTER_STATUS_COLORS as Record<string, string>)[shelter.status] || '#888',
                  fillOpacity: 0.9, weight: 2, color: '#fff',
                }}>
                <Popup>
                  <div style={{ fontSize: 14 }}>
                    <Text size="sm" fw={700} mb={2}>{shelter.name}</Text>
                    <Badge size="xs" variant="light"
                      color={shelter.status === 'open' ? 'var(--color-success)' : shelter.status === 'closed' ? 'var(--color-error)' : '#939598'}>
                      {shelter.status}
                    </Badge>
                    <Text size="xs">Capacity: {shelter.currentPopulation}/{shelter.capacity}</Text>
                  </div>
                </Popup>
              </CircleMarker>
            ))}

            {/* Assessment markers */}
            {assessmentMarkers.map(a => (
              <CircleMarker key={`asmt-${a.id}`} center={[a.latitude!, a.longitude!]} radius={5}
                pathOptions={{
                  fillColor: SEVERITY_COLORS[a.damageCategory || 'affected'] || '#888',
                  fillOpacity: 0.8, weight: 1, color: '#fff',
                }}>
                <Popup>
                  <div style={{ fontSize: 14 }}>
                    <Text size="sm" fw={700}>{a.address || `Assessment #${a.id}`}</Text>
                    <Badge size="xs" variant="light">{a.damageCategory || 'assessed'}</Badge>
                  </div>
                </Popup>
              </CircleMarker>
            ))}

            {/* Camera markers (511) */}
            {showCameras && cameras.map(cam => {
              const inZone = incidentZones.some(z => z.geometry && isInsideZone(cam.lat, cam.lng, z.geometry));
              return (
                <CircleMarker key={`cam-${cam.id}`} center={[cam.lat, cam.lng]} radius={6}
                  pathOptions={{
                    fillColor: inZone ? '#4338ca' : '#818cf8',
                    fillOpacity: 0.9, weight: inZone ? 3 : 1.5,
                    color: inZone ? '#B12525' : '#fff',
                  }}>
                  <Popup minWidth={220}>
                    <div style={{ fontSize: 14 }}>
                      <Group gap={4} mb={2}>
                        <IconCamera size={12} color="#4338ca" />
                        <Text size="sm" fw={700}>{cam.name || cam.location}</Text>
                      </Group>
                      {cam.roadway && <Badge size="xs" variant="light" color="var(--data-viz-100)" mb={4}>{cam.roadway}</Badge>}
                      {cam.direction && <Badge size="xs" variant="light" color="#939598" ml={4} mb={4}>{cam.direction}</Badge>}
                      {inZone && <Badge size="xs" color="var(--color-error)" variant="light" ml={4} mb={4}>In Zone</Badge>}
                      {cam.imageUrl && (
                        <AutoRefreshImage src={cam.imageUrl} alt={cam.name}
                          style={{ maxWidth: 240, marginTop: 8 }} />
                      )}
                      <Text size="xs" c="dimmed" mt={4}>Source: 511 — refreshing every 5s</Text>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

            {/* Road intersection markers (Overpass) */}
            {showClosures && closures.map(c => {
              const inZone = incidentZones.some(z => z.geometry && isInsideZone(c.lat, c.lng, z.geometry));
              return (
                <CircleMarker key={`closure-${c.id}`} center={[c.lat, c.lng]} radius={5}
                  pathOptions={{
                    fillColor: inZone ? '#B12525' : '#fca5a5',
                    fillOpacity: 0.85, weight: inZone ? 2 : 1,
                    color: inZone ? '#7f1d1d' : '#fff',
                  }}>
                  <Popup minWidth={220}>
                    <div style={{ fontSize: 14 }}>
                      <Group gap={4} mb={2}>
                        <IconRoadSign size={12} color="#B12525" />
                        <Text size="sm" fw={700}>{c.name}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">{c.streets.join(' × ')}</Text>
                      <Group gap={4} mt={4}>
                        <Badge size="xs" variant="light" color="var(--color-error)">Priority {c.priority}</Badge>
                        <Badge size="xs" variant="light" color="#939598">{c.roadType}</Badge>
                        {c.direction && <Badge size="xs" variant="light">{c.direction}</Badge>}
                      </Group>
                      {inZone && <Badge size="xs" color="var(--color-error)" variant="filled" mt={4}>In Zone</Badge>}
                      <Text size="xs" c="dimmed" mt={4}>Source: OpenStreetMap</Text>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

            {/* POI markers (HERE Browse) */}
            {showPois && pois.map(p => {
              const inZone = incidentZones.some(z => z.geometry && isInsideZone(p.lat, p.lng, z.geometry));
              return (
                <CircleMarker key={`poi-${p.id}`} center={[p.lat, p.lng]} radius={6}
                  pathOptions={{
                    fillColor: inZone ? '#7c3aed' : '#a78bfa',
                    fillOpacity: 0.85, weight: inZone ? 3 : 1.5,
                    color: inZone ? '#B12525' : '#fff',
                  }}>
                  <Popup minWidth={220}>
                    <div style={{ fontSize: 14 }}>
                      <Group gap={4} mb={2}>
                        <IconMapPin size={12} color="#7c3aed" />
                        <Text size="sm" fw={700}>{p.name}</Text>
                      </Group>
                      {p.categoryLabel && (
                        <Badge size="xs" variant="filled" color="var(--data-viz-650)" mb={4}>
                          {p.categoryIcon} {p.categoryLabel}
                        </Badge>
                      )}
                      {inZone && <Badge size="xs" color="var(--color-error)" variant="light" ml={4} mb={4}>In Zone</Badge>}
                      {p.address && <Text size="xs" c="dimmed" mb={2}>{p.address}</Text>}
                      {p.phone && (
                        <Group gap={4} mb={2}>
                          <IconPhone size={11} color="#888" />
                          <Text size="xs" component="a" href={`tel:${p.phone}`} c="var(--color-primary-700)">{formatPhone(p.phone)}</Text>
                        </Group>
                      )}
                      <Text size="xs" c="dimmed" mt={4}>Source: HERE Maps</Text>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        )}

        {/* Layer panel overlay */}
        {showLayerPanel && (
          <Paper withBorder shadow="md" p="sm" style={{
            position: 'absolute', top: 8, left: 8, zIndex: 150,
            width: 200, background: 'white',
            maxHeight: 'calc(100% - 16px)', overflowY: 'auto',
          }}>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">Data Layers</Text>
              <ActionIcon size="xs" variant="subtle" color="#939598" onClick={() => setShowLayerPanel(false)}>
                <IconX size={12} />
              </ActionIcon>
            </Group>
            <Stack gap="xs">
              <Switch size="xs" label={`Businesses${showBusinesses && orgMarkers.length ? ` (${orgMarkers.length})` : ''}`}
                checked={showBusinesses} onChange={e => setShowBusinesses(e.currentTarget.checked)} color="#03AC9E" />
              <Switch size="xs" label={`Shelters${showShelters && shelterMarkers.length ? ` (${shelterMarkers.length})` : ''}`}
                checked={showShelters} onChange={e => setShowShelters(e.currentTarget.checked)} color="var(--color-success)" />
              <Switch size="xs" label={`Assessments${showAssessments && assessmentMarkers.length ? ` (${assessmentMarkers.length})` : ''}`}
                checked={showAssessments} onChange={e => setShowAssessments(e.currentTarget.checked)} color="#F86A0B" />
              <Switch size="xs" label={`POIs${pois.length ? ` (${pois.length})` : ''}`}
                checked={showPois} onChange={e => setShowPois(e.currentTarget.checked)} color="var(--data-viz-650)" />
              <Switch size="xs" label={`Cameras${cameras.length ? ` (${cameras.length})` : ''}`}
                checked={showCameras} onChange={e => setShowCameras(e.currentTarget.checked)} color="var(--data-viz-100)" />
              <Switch size="xs" label={`Roads${closures.length ? ` (${closures.length})` : ''}`}
                checked={showClosures} onChange={e => setShowClosures(e.currentTarget.checked)} color="var(--color-error)" />
            </Stack>
            {showBusinesses && orgsInZone.size > 0 && (
              <>
                <Divider my="xs" />
                <Text size="xs" c="var(--color-error)" fw={500}>{orgsInZone.size} businesses in active zones</Text>
              </>
            )}
          </Paper>
        )}

        {/* Captured panel (top right) — grouped by type */}
        {(poisInZone.length > 0 || camerasInZone.length > 0 || closuresInZone.length > 0) && (
          <Paper withBorder shadow="md" p="sm" style={{
            position: 'absolute', top: 16, right: 16, zIndex: 150,
            background: 'rgba(255,255,255,0.97)', width: 340, maxHeight: '75vh',
            display: 'flex', flexDirection: 'column',
          }}>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={700} tt="uppercase" c="var(--data-viz-650)">
                Captured ({poisInZone.length + camerasInZone.length + closuresInZone.length})
              </Text>
              <Group gap={4}>
                {poisInZone.length > 0 && <Badge size="xs" color="var(--data-viz-650)" variant="light">{poisInZone.length} POI</Badge>}
                {camerasInZone.length > 0 && <Badge size="xs" color="var(--data-viz-100)" variant="light">{camerasInZone.length} cam</Badge>}
                {closuresInZone.length > 0 && <Badge size="xs" color="var(--color-error)" variant="light">{closuresInZone.length} road</Badge>}
              </Group>
            </Group>
            <div style={{ overflowY: 'auto', flex: 1 }}>

              {/* POIs grouped by category */}
              {poisInZoneByCategory.map(group => (
                <div key={group.key} style={{ marginBottom: 8 }}>
                  <Group gap={4} mb={4} style={{ position: 'sticky', top: 0, background: 'white', paddingBottom: 2, borderBottom: '2px solid #ede9fe' }}>
                    <Text size="xs" fw={700}>{group.icon} {group.label}</Text>
                    <Badge size="xs" color="var(--data-viz-650)" variant="light">{group.items.length}</Badge>
                  </Group>
                  {group.items.map(p => (
                    <div key={p.id} style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <Text size="xs" fw={500} lineClamp={1}>{p.name}</Text>
                      {p.address && (
                        <Text size="xs" c="dimmed" lineClamp={1}>{p.address}</Text>
                      )}
                      <Text size="xs" c="var(--data-viz-650)" lineClamp={1}>in: {p.zoneNames.join(', ')}</Text>
                    </div>
                  ))}
                </div>
              ))}

              {/* Cameras */}
              {camerasInZone.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Group gap={4} mb={4} style={{ position: 'sticky', top: 0, background: 'white', paddingBottom: 2, borderBottom: '2px solid #e0e7ff' }}>
                    <Text size="xs" fw={700}>📹 Traffic Cameras</Text>
                    <Badge size="xs" color="var(--data-viz-100)" variant="light">{camerasInZone.length}</Badge>
                  </Group>
                  {camerasInZone.map(cam => (
                    <div key={`cam-${cam.id}`} style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <Text size="xs" fw={500} lineClamp={1}>{cam.name || cam.location}</Text>
                      {(cam.roadway || cam.direction) && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {cam.roadway}{cam.roadway && cam.direction ? ' · ' : ''}{cam.direction}
                        </Text>
                      )}
                      <Text size="xs" c="var(--data-viz-650)" lineClamp={1}>in: {cam.zoneNames.join(', ')}</Text>
                    </div>
                  ))}
                </div>
              )}

              {/* Road intersections */}
              {closuresInZone.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Group gap={4} mb={4} style={{ position: 'sticky', top: 0, background: 'white', paddingBottom: 2, borderBottom: '2px solid #fee2e2' }}>
                    <Text size="xs" fw={700}>🚧 Road Intersections</Text>
                    <Badge size="xs" color="var(--color-error)" variant="light">{closuresInZone.length}</Badge>
                  </Group>
                  {closuresInZone.map(c => (
                    <div key={`closure-${c.id}`} style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <Text size="xs" fw={500} lineClamp={1}>{c.name}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{c.streets.join(' × ')}</Text>
                      <Text size="xs" c="var(--data-viz-650)" lineClamp={1}>in: {c.zoneNames.join(', ')}</Text>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </Paper>
        )}

        {/* Legend */}
        <Paper withBorder shadow="sm" p="xs" style={{
          position: 'absolute', bottom: 32, left: 16, zIndex: 150,
          background: 'rgba(255,255,255,0.95)', fontSize: 12, minWidth: 160,
          pointerEvents: 'none',
        }}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4}>Legend</Text>

          {/* Zone types */}
          <Text size="xs" fw={600} mb={2}>Zones</Text>
          {Object.entries(ZONE_TYPE_META).map(([key, meta]) => {
            const count = incidentZones.filter(z => z.zoneType === key).length;
            if (count === 0) return null;
            return (
              <Group key={key} gap={6} mb={2}>
                <div style={{ width: 12, height: 12, borderRadius: 4, backgroundColor: meta.color, opacity: 0.7, flexShrink: 0 }} />
                <Text size="xs">{meta.label} ({count})</Text>
              </Group>
            );
          })}

          {/* Data layers */}
          {(showBusinesses || showShelters || showAssessments) && (
            <>
              <Text size="xs" fw={600} mt={6} mb={2}>Layers</Text>
              {showBusinesses && (
                <Group gap={6} mb={2}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#0891b2' }} />
                  <Text size="xs">Business</Text>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#0891b2', border: '2px solid #B12525' }} />
                  <Text size="xs">In Zone</Text>
                </Group>
              )}
              {showShelters && (
                <Group gap={6} mb={2}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#16a34a' }} />
                  <Text size="xs">Shelter (open)</Text>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#B12525' }} />
                  <Text size="xs">Closed</Text>
                </Group>
              )}
              {showAssessments && (
                <Group gap={6} mb={2}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#16a34a' }} />
                  <Text size="xs">Minor</Text>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ea580c' }} />
                  <Text size="xs">Major</Text>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#B12525' }} />
                  <Text size="xs">Destroyed</Text>
                </Group>
              )}
            </>
          )}
        </Paper>
      </div>
    </div>
  );
}
